import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { BrainCircuit, Mic2, Volume2, Code, BarChart3, Cog, Info } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SiteHeader } from "@/components/site-header";

export const AssistantConfigSkeleton = () => {
    return (
        <>
            {/* SiteHeader Skeleton */}
            <SiteHeader
                title={<Skeleton className="h-7 w-48" />}
                subtitle={<Skeleton className="h-4 w-64" />}
                backButton={<Skeleton className="h-9 w-9 rounded-md" />}
                children={
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-9 w-9 rounded-md" /> {/* Delete button */}
                        <Skeleton className="h-9 w-9 rounded-md" /> {/* Export button */}
                        <Skeleton className="h-9 w-9 rounded-md" /> {/* Import button */}
                        <Skeleton className="h-9 w-[140px] rounded-md" /> {/* Talk with Assistant */}
                        <Skeleton className="h-9 w-[100px] rounded-md" /> {/* Save button */}
                    </div>
                }
            />

            <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-6 md:px-8 md:py-8">
                    {/* Tabs Section */}
                    <Tabs defaultValue="information" className="space-y-4">
                        <div className="flex items-center justify-between">
                            <TabsList className="h-11">
                                <TabsTrigger value="information" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <Info className="h-4 w-4" />
                                    Information
                                </TabsTrigger>
                                <TabsTrigger value="behavior" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <BrainCircuit className="h-4 w-4" />
                                    Behavior
                                </TabsTrigger>
                                <TabsTrigger value="voice" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <Volume2 className="h-4 w-4" />
                                    Voice
                                </TabsTrigger>
                                <TabsTrigger value="transcriber" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <Mic2 className="h-4 w-4" />
                                    Transcriber
                                </TabsTrigger>
                                <TabsTrigger value="functions" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <Code className="h-4 w-4" />
                                    Functions
                                </TabsTrigger>
                                <TabsTrigger value="advanced" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <Cog className="h-4 w-4" />
                                    Advanced
                                </TabsTrigger>
                                <TabsTrigger value="analysis" className="gap-2 px-4 pointer-events-none opacity-50">
                                    <BarChart3 className="h-4 w-4" />
                                    Analysis
                                </TabsTrigger>
                            </TabsList>
                        </div>

                        {/* Tab Content Skeleton */}
                        <div className="space-y-4">
                            {/* Information Tab Content Skeleton */}
                            <div className="grid grid-cols-12 gap-6">
                                {/* Left Column - Main Configuration */}
                                <div className="col-span-8 space-y-6">
                                    <Card>
                                        <CardHeader className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-5 w-5" /> {/* Icon */}
                                                <div>
                                                    <Skeleton className="h-6 w-48 mb-1" /> {/* Title */}
                                                    <Skeleton className="h-4 w-72" /> {/* Description */}
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {/* Name Field */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-24" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Input */}
                                            </div>

                                            {/* Assistant ID Field */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-32" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Input */}
                                            </div>

                                            {/* Created/Updated Fields */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-28" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Input */}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Right Column - Model Settings */}
                                <div className="col-span-4 space-y-4">
                                    <Card>
                                        <CardHeader>
                                            <div className="flex items-center gap-2">
                                                <Skeleton className="h-5 w-5" /> {/* Icon */}
                                                <div>
                                                    <Skeleton className="h-6 w-32 mb-1" /> {/* Title */}
                                                    <Skeleton className="h-4 w-48" /> {/* Description */}
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {/* Model Selection */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-20" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Select */}
                                            </div>

                                            {/* Temperature */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-24" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Slider */}
                                            </div>

                                            {/* Emotionality */}
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-4 w-4" /> {/* Icon */}
                                                    <Skeleton className="h-4 w-24" /> {/* Label */}
                                                </div>
                                                <Skeleton className="h-10 w-full" /> {/* Slider */}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            </div>
                        </div>
                    </Tabs>
                </div>
            </div>
        </>    
    );
};